import React, { useEffect, useRef, useState } from 'react';

// Module-level singleton so multiple mounts share one load attempt.
let mapsLoadPromise = null;

function loadGoogleMaps() {
  if (window.google?.maps?.importLibrary) return Promise.resolve(true);
  if (mapsLoadPromise) return mapsLoadPromise;

  const key = process.env.REACT_APP_GOOGLE_API_KEY;
  if (!key) {
    console.warn('[LocationInput] Missing REACT_APP_GOOGLE_API_KEY — falling back to plain input');
    return Promise.resolve(false);
  }

  // Another script (e.g. travelTimeService) may have already injected Maps.
  const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');

  mapsLoadPromise = new Promise((resolve) => {
    if (existingScript) {
      // Poll until the bootstrap API surface is ready.
      let attempts = 0;
      const poll = setInterval(() => {
        attempts++;
        if (window.google?.maps?.importLibrary) {
          clearInterval(poll);
          resolve(true);
        } else if (attempts > 100) { // 10 s timeout
          clearInterval(poll);
          console.error('[LocationInput] Timed out waiting for Maps bootstrap API');
          resolve(false);
        }
      }, 100);
      return;
    }

    // Google's official dynamic bootstrap snippet. Creates google.maps.importLibrary
    // as a stub immediately so importLibrary is callable before the lazy script loads.
    // If the underlying script fails, importLibrary('places') will reject and the
    // existing try/catch in the effect will set status='failed' → fallback input shows.
    try {
      ((g) => {
        var h, a, k,
          p = 'The Google Maps JavaScript API',
          c = 'google', l = 'importLibrary', q = '__ib__',
          m = document, b = window;
        b = b[c] || (b[c] = {});
        var d = b.maps || (b.maps = {}),
          r = new Set(),
          e = new URLSearchParams(),
          u = () => h || (h = new Promise(async (f, n) => {
            await (a = m.createElement('script'));
            e.set('libraries', [...r] + '');
            for (k in g) e.set(k.replace(/[A-Z]/g, t => '_' + t[0].toLowerCase()), g[k]);
            e.set('callback', c + '.maps.' + q);
            a.src = `https://maps.googleapis.com/maps/api/js?${e}`;
            d[q] = f;
            a.onerror = () => { h = n(new Error(p + ' could not load.')); };
            a.nonce = m.querySelector('script[nonce]')?.nonce || '';
            m.head.append(a);
          }));
        d[l] ? console.warn(p + ' only loads once. Ignoring:', g)
              : (d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n)));
      })({ key, v: 'weekly' });
      resolve(true);
    } catch (err) {
      console.error('[LocationInput] Maps bootstrap failed:', err.message);
      resolve(false);
    }
  });

  return mapsLoadPromise;
}

/**
 * Location input with PlaceAutocompleteElement (new Places API).
 * Always renders a usable plain text input as fallback — location entry
 * never breaks even when Maps is unavailable.
 *
 * Props: value, onChange(string), placeholder, id, className
 */
export default function LocationInput({ value, onChange, placeholder, id, className }) {
  const containerRef  = useRef(null);
  const acElementRef  = useRef(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'failed'
  const [showManual, setShowManual] = useState(false);

  useEffect(() => {
    let cancelled = false;

    loadGoogleMaps()
      .then(async (loaded) => {
        if (!loaded || cancelled) { setStatus('failed'); return; }

        try {
          const { PlaceAutocompleteElement } = await window.google.maps.importLibrary('places');
          if (cancelled || !containerRef.current) return;

          const element = new PlaceAutocompleteElement({
            componentRestrictions: { country: 'gb' },
          });

          element.style.width = '100%';
          // Force a light colour scheme so the element's shadow-DOM input renders
          // dark text on white. Without this the shadow root inherits the page/OS
          // color-scheme and can render black-on-black. color-scheme is one of the
          // few properties that inherits across the shadow boundary by design.
          element.style.colorScheme = 'light';

          element.addEventListener('gmp-placeselect', async ({ place }) => {
            try {
              await place.fetchFields({ fields: ['formattedAddress', 'displayName'] });
              onChange(place.formattedAddress || place.displayName?.text || '');
            } catch (e) {
              console.error('[LocationInput] fetchFields failed:', e.message);
            }
          });

          containerRef.current.appendChild(element);
          acElementRef.current = element;
          setStatus('ready');
        } catch (err) {
          console.error('[LocationInput] PlaceAutocompleteElement init failed:', err.message);
          if (!cancelled) setStatus('failed');
        }
      })
      .catch((err) => {
        console.error('[LocationInput] Maps load error:', err.message);
        if (!cancelled) setStatus('failed');
      });

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When value is a URL (meeting link from Zoom/Meet/Teams), switch to the plain
  // input — PlaceAutocompleteElement is for address autocomplete only and has
  // shadow-DOM styling that breaks URL display (black-on-black).
  useEffect(() => {
    if (value && (value.startsWith('http://') || value.startsWith('https://'))) {
      setShowManual(true);
    }
  }, [value]);

  // When value is set programmatically (e.g. Zoom URL from meeting modal),
  // push it into the AC element's input so the field reflects the change.
  useEffect(() => {
    const el = acElementRef.current;
    if (!el || showManual) return;
    try { el.value = value || ''; } catch (_) {}
  }, [value, showManual]);

  const autocompleteActive = status === 'ready' && !showManual;

  return (
    <div className="location-input-wrapper" style={{ position: 'relative', colorScheme: 'light' }}>
      {/* PlaceAutocompleteElement mounts here; hidden when not active */}
      <div
        ref={containerRef}
        style={autocompleteActive ? undefined : { display: 'none' }}
      />

      {/* Plain text input — shown during load, on failure, or when manual mode active */}
      {!autocompleteActive && (
        <input
          id={id}
          type="text"
          className={className}
          placeholder={placeholder || 'Enter a physical address or paste a link'}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
        />
      )}

      {/* Toggle — only shown after a successful load so failed state stays silent */}
      {status === 'ready' && (
        <button
          type="button"
          className="location-manual-toggle"
          onClick={() => setShowManual(m => !m)}
          style={{ fontSize: '0.75rem', marginTop: '0.25rem', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-text-secondary, #888)' }}
        >
          {showManual ? '← Use autocomplete' : 'Type manually instead'}
        </button>
      )}
    </div>
  );
}
